import { RejectModal } from "@/entity/modal";
import {
	AccessRouteForm,
	AdditionalInfoForm,
	BasicInfoForm,
	CareerInformationForm,
	CorporationForm,
	ETCInfoForm,
	EducationForm,
	FCRegistrationForm,
	FamilyRelationshipForm,
	GStarClubForm,
	InsuranceExperienceForm,
	PersonalInfoForm,
	ProfessionalQualificationForm,
	TaxAccountantInfoForm
} from "@/features/approval";
import { useApproveTaxAccountant, useGetTaxAccountantForRequestId, useRejectTaxAccountant } from "@/shared/api";
import {
	ITaxAccountantRequestDetailV2ResModel,
	TaxAccountantRequestDetailV2ResModel
} from "@/shared/api/tax-accountant/dto/TaxAccountantRequestDetailV2ResModel";
import { useModal, vars } from "@/shared/lib";

import { Button, Center, Column, Row, Toast } from "@/shared/ui";
import { standardSchemaValidator, useForm } from "@tanstack/react-form";
import { createFileRoute, useParams, useRouter } from "@tanstack/react-router";

export const Route = createFileRoute("/(auth)/_layout/approval/$requestId")({
	component: TaxAccountantApproval
});

function TaxAccountantApproval() {
	const { openModal, closeModal } = useModal();
	const { requestId } = useParams({ from: "/(auth)/_layout/approval/$requestId" });
	const router = useRouter();

	const { data } = useGetTaxAccountantForRequestId({ requestId });
	const { mutate: mutateApproval, reset: resetApproval } = useApproveTaxAccountant();
	const { mutate: mutateReject, reset: resetReject } = useRejectTaxAccountant();

	const form = useForm({
		defaultValues:
			data.requestType === "INSERT"
				? ({
					type: "INSERT" as const,
					before: {
						...data.registerDto,
						basicInformation: {
							...data.registerDto.basicInformation,
							managers: [
								{
									...data.registerDto.basicInformation.manager,
									managerId: data.registerDto.basicInformation.manager?.id || -1
								}
							]
						},
						corporationInformation: {
							...data.registerDto.corporationInformation,
							coordinate: {
								latitude: data.registerDto.corporationInformation?.latitude || -1,
								longitude: data.registerDto.corporationInformation?.longitude || -1
							},
							familyInformations: []
						}
					},
					after: {
						...data.registerDto,
						basicInformation: {
							...data.registerDto.basicInformation,
							managers: [
								{
									...data.registerDto.basicInformation.manager,
									managerId: data.registerDto.basicInformation.manager?.id || -1
								}
							]
						},
						corporationInformation: {
							...data.registerDto.corporationInformation,
							coordinate: {
								latitude: data.registerDto.corporationInformation?.latitude || -1,
								longitude: data.registerDto.corporationInformation?.longitude || -1
							},
							familyInformations: data.registerDto.corporationInformation?.familyInformations
						}
					}
				} as unknown as ITaxAccountantRequestDetailV2ResModel)
				: ({
					type: "UPDATE" as const,
					before: {
						...data.modifySnapshotDto.before,
						corporationInformation: {
							...data.modifySnapshotDto.before.corporationInformation,
							coordinate: {
								latitude: data.modifySnapshotDto.before.corporationInformation?.latitude || -1,
								longitude: data.modifySnapshotDto.before.corporationInformation?.longitude || -1
							}
						}
					},
					after: {
						...data.modifySnapshotDto.after,
						corporationInformation: {
							...data.modifySnapshotDto.after.corporationInformation,
							coordinate: {
								latitude: data.modifySnapshotDto.after.corporationInformation?.latitude || -1,
								longitude: data.modifySnapshotDto.after.corporationInformation?.longitude || -1
							}
						}
					}
				} as ITaxAccountantRequestDetailV2ResModel),
		asyncAlways: true,
		asyncDebounceMs: 700,
		validatorAdapter: standardSchemaValidator(),
		validators: {
			onChangeAsync: TaxAccountantRequestDetailV2ResModel.model,
			onChangeAsyncDebounceMs: 700,
			onBlurAsyncDebounceMs: 700
		},
		onSubmit: async () => {
			mutateApproval(
				{ requestId },
				{
					onSuccess: () => {
						Toast.success("승인되었습니다.");
						router.history.back();
					},
					onError: (error) => {
						console.error("세무사 승인  error", error.data);
						resetApproval();
						// Toast.error("승인실패되었습니다");
					}
				}
			);
		}
	});

	return (
		<Column
			style={{
				justifyContent: "flex-start",
				alignItems: "flex-start",
				gap: vars.size[48],
				paddingBottom: vars.size[48]
			}}
		>
			<Row
				fullWidth
				style={{
					maxWidth: 1500,
					justifyContent: "flex-start",
					alignItems: "flex-start",
					gap: vars.size[48]
				}}
			>
				<Column
					fullWidth
					style={{
						minWidth: 450,
						justifyContent: "flex-start",
						alignItems: "flex-start",
						gap: vars.size[32]
					}}
				>
					<BasicInfoForm form={form} />
					<ProfessionalQualificationForm form={form} />
					<CorporationForm form={form} />
					<InsuranceExperienceForm form={form} />
					<AccessRouteForm form={form} />
					<FamilyRelationshipForm form={form} />
					<GStarClubForm form={form} />
				</Column>

				<Column
					fullWidth
					style={{
						minWidth: 450,
						justifyContent: "flex-start",
						alignItems: "flex-start",
						gap: vars.size[32]
					}}
				>
					<PersonalInfoForm form={form} />
					<AdditionalInfoForm form={form} />
					<ETCInfoForm form={form} />
				</Column>

				<Column
					fullWidth
					style={{
						minWidth: 450,
						justifyContent: "flex-start",
						alignItems: "flex-start",
						gap: vars.size[32]
					}}
				>
					<CareerInformationForm form={form} />
					<EducationForm form={form} />
					<TaxAccountantInfoForm form={form} />
					<FCRegistrationForm form={form} />
				</Column>
			</Row>

			<Center
				fullWidth
				style={{
					flexDirection: "row",
					paddingBlock: vars.size[32],
					paddingInline: vars.size[16],
					gap: vars.size[8]
				}}
			>
				<Button color={"third"} size="large" onClick={() => router.history.back()}>
					{"뒤로가기"}
				</Button>
				<Button
					size="large"
					color="warn"
					onClick={() => {
						openModal(
							<RejectModal
								onClose={() => {
									closeModal;
								}}
								onConfirm={(rejectReason) => {
									mutateReject(
										{ requestId, rejectReason },
										{
											onSuccess: () => {
												Toast.success("반려되었습니다");
												router.history.back();
											},
											onError: (error) => {
												if (Array.isArray(error.data)) {
													for (const data of error.data) {
														Toast.error(`${data.reason}`);
													}
												}

												resetReject();
											}
										}
									);
									closeModal();
								}}
							/>
						);
					}}
				>
					{"반려하기"}
				</Button>
				<Button
					size="large"
					onClick={(e) => {
						e.stopPropagation();
						e.preventDefault();
						form.handleSubmit();
					}}
				>
					{"승인완료"}
				</Button>
			</Center>
		</Column>
	);
}
